import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw } from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  ConfirmDialog,
  DangerConfirmDialog,
  ErrorState,
  FormField,
  Input,
  SkeletonText,
  StatusPill,
  Switch,
  Table,
  TableContainer,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import type { DangerConfirmationCredentials } from "@/components/ui/DangerConfirmDialog";
import { useConfigVersions, useCurrentConfig, useRollbackConfigVersion, useUpdateConfig } from "@/lib/queries";
import { toConfigInput } from "@/lib/utils/config";
import { APP_MODE_META } from "@/lib/utils/statusMeta";
import { formatDateTime } from "@/lib/utils/format";
import { ApiError, configApi, DANGEROUS_APP_MODES, type GlobalConfigVersion } from "@/lib/api";

export function VersionsPage() {
  const configQuery = useCurrentConfig();
  const versionsQuery = useConfigVersions();
  const updateConfig = useUpdateConfig();
  const rollback = useRollbackConfigVersion();
  const { toast } = useToast();

  const config = configQuery.data;

  const [minimumVersion, setMinimumVersion] = useState("");
  const [minimumVersionCode, setMinimumVersionCode] = useState(0);
  const [pendingForceUpdateOn, setPendingForceUpdateOn] = useState(false);
  const [savingVersionFields, setSavingVersionFields] = useState(false);
  const [rollbackTarget, setRollbackTarget] = useState<GlobalConfigVersion | null>(null);

  useEffect(() => {
    if (config) {
      setMinimumVersion(config.minimumVersion);
      setMinimumVersionCode(config.minimumVersionCode);
    }
  }, [config]);

  async function saveMinimumVersion() {
    if (!config) return;
    setSavingVersionFields(true);
    try {
      await updateConfig.mutateAsync({
        ...toConfigInput(config),
        minimumVersion,
        minimumVersionCode,
        changeReason: "Actualizar version minima",
      });
      toast({ title: "Version minima actualizada", variant: "success" });
    } catch {
      toast({ title: "No se pudo actualizar la version minima", variant: "error" });
    } finally {
      setSavingVersionFields(false);
    }
  }

  function describeConfigError(error: unknown) {
    if (error instanceof ApiError) {
      if (error.status === 401) return "La sesion ha caducado. Vuelve a iniciar sesion.";
      if (error.status === 403) return "El backend exige reautenticacion y TOTP validos para este cambio.";
      if (error.status === 409) return "La configuracion cambio mientras operabas. Recarga y revisa el estado actual.";
      if (error.code === "network_error") return error.message;
      return error.message;
    }
    if (error instanceof Error && error.message) return error.message;
    return "No se pudo completar la operacion. Intentalo de nuevo.";
  }

  async function confirmationTokenForForceUpdate(credentials: DangerConfirmationCredentials | undefined) {
    if (!credentials) throw new Error("Introduce contrasena administrativa y codigo TOTP.");
    const result = await configApi.createEmergencyConfirmation({
      password: credentials.password,
      totpCode: credentials.totpCode,
      targetMode: "force_update",
    });
    return result.confirmationToken;
  }

  async function setForceUpdate(value: boolean, credentials?: DangerConfirmationCredentials) {
    if (!config) return;
    try {
      const confirmationToken = value ? await confirmationTokenForForceUpdate(credentials) : undefined;
      await updateConfig.mutateAsync({
        ...toConfigInput(config),
        forceUpdate: value,
        changeReason: value ? "Activar actualizacion forzosa" : "Desactivar actualizacion forzosa",
        confirmationToken,
      });
      toast({
        title: value ? "Actualizacion forzosa activada" : "Actualizacion forzosa desactivada",
        variant: value ? "info" : "success",
      });
      setPendingForceUpdateOn(false);
    } catch (error) {
      const message = describeConfigError(error);
      toast({ title: "No se pudo actualizar force update", description: message, variant: "error" });
      throw new Error(message);
    }
  }

  async function handleRollback() {
    if (!rollbackTarget) return;
    await rollback.mutateAsync(rollbackTarget.id);
    toast({ title: `Restaurado a la revision #${rollbackTarget.revision}`, variant: "success" });
  }

  const versions = versionsQuery.data ?? [];
  const rollbackIsDangerous = rollbackTarget ? DANGEROUS_APP_MODES.includes(rollbackTarget.appMode) : false;

  return (
    <div>
      <PageHeader
        title="Versiones"
        description="Version minima soportada, actualizacion forzosa e historial de configuracion."
      />

      <div className="space-y-4">
        <Card>
          <CardHeader title="Requisitos de version" />
          <CardBody>
            {configQuery.isLoading && <SkeletonText lines={3} />}
            {configQuery.isError && (
              <ErrorState description="No se pudo cargar la configuracion." onRetry={() => configQuery.refetch()} />
            )}
            {config && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <FormField label="Version minima" hint="Formato semver, ej. 1.4.0">
                    {(field) => (
                      <Input {...field} value={minimumVersion} onChange={(e) => setMinimumVersion(e.target.value)} />
                    )}
                  </FormField>
                  <FormField label="Codigo de version minimo">
                    {(field) => (
                      <Input
                        {...field}
                        type="number"
                        value={minimumVersionCode}
                        onChange={(e) => setMinimumVersionCode(Number(e.target.value))}
                      />
                    )}
                  </FormField>
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    loading={savingVersionFields}
                    onClick={saveMinimumVersion}
                    disabled={
                      minimumVersion === config.minimumVersion && minimumVersionCode === config.minimumVersionCode
                    }
                  >
                    Guardar version minima
                  </Button>
                </div>

                <div className="flex items-center justify-between gap-4 border-t border-ink/10 pt-4">
                  <div>
                    <p className="text-sm font-medium text-ink">Actualizacion forzosa</p>
                    <p className="text-sm text-ink-muted">
                      Bloquea a las personas usuarias por debajo de la version minima.
                    </p>
                  </div>
                  <Switch
                    checked={config.forceUpdate}
                    onCheckedChange={(next) => {
                      if (next) setPendingForceUpdateOn(true);
                      else void setForceUpdate(false).catch(() => undefined);
                    }}
                    aria-label="Actualizacion forzosa"
                  />
                </div>

                <div className="border-t border-ink/10 pt-4 text-sm text-ink-muted">
                  Mensaje de mantenimiento:{" "}
                  <span className="text-ink">{config.maintenanceMessage || "sin definir"}</span>.{" "}
                  <Link to="/app-status" className="font-medium text-brand-700 hover:underline">
                    Editar en Estado de la app
                  </Link>
                </div>
              </div>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Historial de versiones" description="Cada cambio crea una nueva revision inmutable." />
          <CardBody className="p-0">
            {versionsQuery.isLoading && (
              <div className="p-5">
                <SkeletonText lines={4} />
              </div>
            )}
            {versionsQuery.isError && (
              <div className="p-5">
                <ErrorState description="No se pudo cargar el historial." onRetry={() => versionsQuery.refetch()} />
              </div>
            )}
            {versions.length > 0 && (
              <TableContainer className="rounded-none border-0">
                <Table>
                  <THead>
                    <tr>
                      <TH>Revision</TH>
                      <TH>Modo</TH>
                      <TH>Fecha</TH>
                      <TH>Autor</TH>
                      <TH>Motivo</TH>
                      <TH className="text-right">Accion</TH>
                    </tr>
                  </THead>
                  <TBody>
                    {versions.map((v) => (
                      <TR key={v.id}>
                        <TD>#{v.revision}</TD>
                        <TD>
                          <StatusPill tone={APP_MODE_META[v.appMode].tone}>{APP_MODE_META[v.appMode].label}</StatusPill>
                        </TD>
                        <TD>{formatDateTime(v.createdAt)}</TD>
                        <TD>{v.createdByEmail ?? "-"}</TD>
                        <TD className="max-w-xs truncate">{v.changeReason ?? "-"}</TD>
                        <TD>
                          <div className="flex justify-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              icon={<RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />}
                              onClick={() => setRollbackTarget(v)}
                              disabled={config?.id === v.id}
                            >
                              Restaurar
                            </Button>
                          </div>
                        </TD>
                      </TR>
                    ))}
                  </TBody>
                </Table>
              </TableContainer>
            )}
          </CardBody>
        </Card>
      </div>

      <DangerConfirmDialog
        open={pendingForceUpdateOn}
        onClose={() => setPendingForceUpdateOn(false)}
        onConfirm={(credentials) => setForceUpdate(true, credentials)}
        title="Activar actualizacion forzosa"
        description="Las personas usuarias por debajo de la version minima quedaran bloqueadas."
        consequences={[
          "Todo dispositivo con una version anterior a la minima perdera acceso hasta actualizar.",
          "El cambio se aplica de inmediato al publicarse.",
        ]}
        confirmationPhrase="forzar actualizacion"
        confirmLabel="Activar"
        credentialsRequired
      />

      {rollbackTarget && (
        rollbackIsDangerous ? (
          <DangerConfirmDialog
            open
            onClose={() => setRollbackTarget(null)}
            onConfirm={handleRollback}
            title={`Restaurar revision #${rollbackTarget.revision}`}
            description="Esta revision histórica queda en modo de riesgo. Restaurarla crea una nueva revision con ese estado."
            consequences={[
              `El modo pasara a "${APP_MODE_META[rollbackTarget.appMode].label}" de inmediato.`,
              "Se crea una nueva revision; el historial anterior no se modifica.",
            ]}
            confirmationPhrase="restaurar"
            confirmLabel="Restaurar version"
          />
        ) : (
          <ConfirmDialog
            open
            onClose={() => setRollbackTarget(null)}
            onConfirm={handleRollback}
            title={`Restaurar revision #${rollbackTarget.revision}`}
            description="Se creara una nueva revision con los valores de esa version historica."
            confirmLabel="Restaurar version"
          />
        )
      )}
    </div>
  );
}
